import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { adminApi } from '../../services/adminApi';
import ApplicantDetail from './ApplicantDetail';
import TestManagement from './TestManagement';
import { useConfirm } from '../../components/ConfirmDialog';
import { useToast } from '../../components/Toast';

const formatPhone = (phone: string) => {
  const d = phone.replace(/\D/g, '');
  if (d.length === 11) return d.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
  if (d.length === 10) return d.replace(/(\d{3})(\d{3,4})(\d{4})/, '$1-$2-$3');
  return phone;
};

const statusLabels: Record<string, string> = {
  DRAFT: '작성중',
  APPLIED: '지원완료',
  AI_REVIEWED: 'AI심사완료',
  WRITTEN_TEST: '서면평가',
  INTERVIEW: '면접',
  OFFER: '오퍼',
  HIRED: '입사확정',
  DOC_REJECTED: '서류불합격',
  WRITTEN_REJECTED: '서면불합격',
  REJECTED: '불합격',
};

const statusColors: Record<string, string> = {
  DRAFT: 'bg-slate-100 text-slate-600',
  APPLIED: 'bg-blue-50 text-blue-600',
  AI_REVIEWED: 'bg-purple-50 text-purple-600',
  WRITTEN_TEST: 'bg-amber-50 text-amber-600',
  INTERVIEW: 'bg-indigo-50 text-indigo-600',
  OFFER: 'bg-green-50 text-green-600',
  HIRED: 'bg-green-100 text-green-700',
  DOC_REJECTED: 'bg-orange-50 text-orange-600',
  WRITTEN_REJECTED: 'bg-rose-50 text-rose-600',
  REJECTED: 'bg-red-50 text-red-600',
};

type TabType = 'campaigns' | 'pipeline' | 'hired' | 'all';

interface Props {
  admin: any;
  onLogout: () => void;
}

const AdminDashboard: React.FC<Props> = ({ admin, onLogout }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const confirm = useConfirm();
  const { toast } = useToast();

  // 탭 상태
  const currentTab = (searchParams.get('tab') as TabType) || 'campaigns';
  const setTab = (tab: TabType, keepCampaignFilter?: boolean) => {
    setSearchParams(tab === 'campaigns' ? {} : { tab });
    setFilterStatus('');
    if (!keepCampaignFilter) setFilterCampaignId('');
    setSearch('');
    setShowDeleted(false);
  };

  // 지원자 상태
  const [stats, setStats] = useState<Record<string, number>>({});
  const [applicants, setApplicants] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCampaignId, setFilterCampaignId] = useState<string>('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showTestAccount, setShowTestAccount] = useState(false);
  const [recruitmentOpen, setRecruitmentOpen] = useState(true);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const isDev = admin?.email === 'sungbae@grkcon.com';

  // 캠페인 상태
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [unassignedCount, setUnassignedCount] = useState(0);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<any>(null);
  const [campaignForm, setCampaignForm] = useState({ name: '', description: '', start_date: '', end_date: '' });
  const [selectedPositionIds, setSelectedPositionIds] = useState<number[]>([]);
  const [availablePositions, setAvailablePositions] = useState<any[]>([]);
  const [newPositionName, setNewPositionName] = useState('');
  const [addingPosition, setAddingPosition] = useState(false);
  const [showClosedCampaigns, setShowClosedCampaigns] = useState(false);

  // 최종합격 탭
  const [hiredApplicants, setHiredApplicants] = useState<any[]>([]);
  const [hiredLoading, setHiredLoading] = useState(false);

  // 시험 관리 (채용 진행 탭 내 통합)
  const [showTestManagement, setShowTestManagement] = useState(false);

  // 지원자 관리 — 삭제된 지원자 표시
  const [showDeleted, setShowDeleted] = useState(false);

  // URL에서 상태 복원
  const selectedId = searchParams.get('id') ? Number(searchParams.get('id')) : null;

  const setSelectedId = (id: number | null) => {
    if (id) {
      setSearchParams({ tab: currentTab, id: String(id) });
    } else {
      setSearchParams(currentTab === 'campaigns' ? {} : { tab: currentTab });
    }
  };

  useEffect(() => {
    loadRecruitmentStatus();
    loadCampaigns();
    loadAvailablePositions();
  }, []);

  useEffect(() => {
    if (currentTab === 'pipeline' || currentTab === 'all') {
      loadStats();
      loadApplicants();
    }
    if (currentTab === 'hired') {
      loadHiredApplicants();
    }
  }, [filterStatus, filterCampaignId, showTestAccount, showDeleted, currentTab]);

  const loadRecruitmentStatus = async () => {
    try {
      const data = await adminApi.getRecruitmentStatus();
      setRecruitmentOpen(data.recruitment_open);
    } catch {}
    if (isDev) {
      try {
        const data = await adminApi.getTestAccountVisible();
        setShowTestAccount(data.test_account_visible);
      } catch {}
    }
  };

  const handleToggleRecruitment = async () => {
    try {
      const data = await adminApi.setRecruitmentOpen(!recruitmentOpen);
      setRecruitmentOpen(data.recruitment_open);
      toast.success(data.recruitment_open ? '채용이 활성화되었습니다.' : '채용이 비활성화되었습니다.');
    } catch {
      toast.error('변경 실패');
    }
  };

  const loadCampaigns = async () => {
    try {
      const data = await adminApi.getCampaigns();
      setCampaigns(data.campaigns);
      setUnassignedCount(data.unassigned_count);
    } catch {}
  };

  const loadAvailablePositions = async () => {
    try {
      const data = await adminApi.getPositionList();
      const sorted = (data.positions || data || []).sort((a: any, b: any) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
      setAvailablePositions(sorted);
    } catch {}
  };

  const handleAddPosition = async () => {
    if (!newPositionName.trim()) return;
    setAddingPosition(true);
    try {
      const created = await adminApi.createPosition(newPositionName.trim());
      setAvailablePositions((prev) => [...prev, created]);
      setSelectedPositionIds((prev) => [...prev, created.id]);
      setNewPositionName('');
      toast.success(`"${created.name}" 포지션이 추가되었습니다.`);
    } catch (err: any) {
      toast.error(err.response?.data?.message || '포지션 추가 실패');
    }
    setAddingPosition(false);
  };

  const handleToggleTestAccount = async () => {
    try {
      const data = await adminApi.setTestAccountVisible(!showTestAccount);
      setShowTestAccount(data.test_account_visible);
    } catch {}
  };

  const loadStats = async () => {
    try {
      const data = await adminApi.getStats();
      setStats(data);
    } catch {}
  };

  const loadHiredApplicants = async () => {
    setHiredLoading(true);
    try {
      const data = await adminApi.getApplicants({
        status: 'HIRED',
        includeTest: showTestAccount || undefined,
      });
      // workspace 등록 상태 확인
      const items = await Promise.all(
        data.items.map(async (a: any) => {
          try {
            const ws = await adminApi.checkWorkspaceStatus(a.id);
            return { ...a, workspace: ws };
          } catch {
            return { ...a, workspace: { registered: false } };
          }
        })
      );
      setHiredApplicants(items);
    } catch {}
    setHiredLoading(false);
  };

  const loadApplicants = async () => {
    setLoading(true);
    try {
      const data = await adminApi.getApplicants({
        status: filterStatus || undefined,
        search: search || undefined,
        includeTest: showTestAccount || undefined,
        campaign_id: filterCampaignId || undefined,
        include_deleted: showDeleted || undefined,
      });
      let filtered = data.items;
      if (showDeleted) {
        // 삭제된 지원자만 표시
        filtered = filtered.filter((a: any) => a.deleted_at);
      } else if (currentTab === 'pipeline') {
        // 채용 진행: 진행중만 (불합격/HIRED 제외)
        const rejectedStatuses = ['HIRED', 'DOC_REJECTED', 'WRITTEN_REJECTED', 'REJECTED'];
        filtered = filtered.filter((a: any) => !rejectedStatuses.includes(a.status));
      }
      // 전체 지원자(all): 필터 없이 모두 표시
      setApplicants(filtered);
      setTotal(filtered.length);
    } catch {}
    setLoading(false);
    setSelectedIds([]);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    loadApplicants();
  };

  // 캠페인 CRUD
  const openCreateCampaign = () => {
    setEditingCampaign(null);
    setCampaignForm({ name: '', description: '', start_date: '', end_date: '' });
    setSelectedPositionIds([]);
    setShowCampaignModal(true);
  };

  const openEditCampaign = (c: any) => {
    setEditingCampaign(c);
    setCampaignForm({
      name: c.name,
      description: c.description || '',
      start_date: c.start_date?.split('T')[0] || '',
      end_date: c.end_date?.split('T')[0] || '',
    });
    // positions가 JSON 배열 문자열이면 파싱, 아니면 빈 배열
    let parsedPositions: number[] = [];
    if (c.positions) {
      try {
        const parsed = JSON.parse(c.positions);
        if (Array.isArray(parsed)) {
          parsedPositions = parsed.map(Number).filter(Boolean);
        }
      } catch {
        parsedPositions = [];
      }
    }
    setSelectedPositionIds(parsedPositions);
    setShowCampaignModal(true);
  };

  const handleSaveCampaign = async () => {
    if (!campaignForm.name.trim() || !campaignForm.start_date || !campaignForm.end_date) return;
    if (campaignForm.start_date > campaignForm.end_date) {
      toast.error('종료일은 시작일 이후여야 합니다.');
      return;
    }
    const payload = {
      ...campaignForm,
      positions: JSON.stringify(selectedPositionIds),
    };
    try {
      if (editingCampaign) {
        await adminApi.updateCampaign(editingCampaign.id, payload);
        toast.success('캠페인이 수정되었습니다.');
      } else {
        await adminApi.createCampaign(payload);
        toast.success('캠페인이 생성되었습니다.');
      }
      setShowCampaignModal(false);
      loadCampaigns();
    } catch (err: any) {
      const msg = err?.response?.data?.message || '캠페인 저장에 실패했습니다.';
      toast.error(msg);
    }
  };

  const handleToggleCampaign = async (id: number, name: string, currentActive: boolean) => {
    const action = currentActive ? '비활성화' : '활성화';
    const ok = await confirm({
      title: `캠페인 ${action}`,
      message: currentActive
        ? `"${name}" 캠페인을 비활성화하시겠습니까?\n비활성 캠페인에는 새 지원자가 배정될 수 없습니다.`
        : `"${name}" 캠페인을 다시 활성화하시겠습니까?`,
      variant: currentActive ? 'danger' : 'default',
      confirmText: action,
    });
    if (!ok) return;
    try {
      if (currentActive) {
        await adminApi.closeCampaign(id);
      } else {
        await adminApi.updateCampaign(id, { is_active: true } as any);
      }
      loadCampaigns();
    } catch {}
  };

  // 지원자 삭제 (3단계 확인)
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteStep, setDeleteStep] = useState(0); // 0: 닫힘, 1: 1차확인, 2: 사유입력

  const handleDeleteApplicant = (applicant: any) => {
    setDeleteTarget(applicant);
    setDeleteReason('');
    setDeleteStep(1);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteReason.trim()) return;
    try {
      await adminApi.softDeleteApplicant(deleteTarget.id, deleteReason);
      toast.success('지원자가 삭제되었습니다.');
      loadApplicants();
      loadStats();
      loadCampaigns();
    } catch {
      toast.error('삭제 실패');
    }
    setDeleteStep(0);
    setDeleteTarget(null);
  };

  // 일괄 캠페인 배정
  const handleBulkAssign = async (campaignId: number) => {
    if (selectedIds.length === 0) return;
    try {
      await adminApi.bulkAssignCampaign(selectedIds, campaignId);
      setSelectedIds([]);
      loadApplicants();
      loadCampaigns();
    } catch {}
  };

  // 체크박스 토글
  const toggleSelect = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === applicants.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(applicants.map((a) => a.id));
    }
  };

  const totalApplicants = Object.values(stats).reduce((a, b) => a + b, 0);
  const activeCampaigns = campaigns.filter((c) => c.is_active);
  const closedCampaigns = campaigns.filter((c) => !c.is_active);

  // 상세 보기
  if (selectedId) {
    return (
      <ApplicantDetail
        id={selectedId}
        onBack={() => { setSelectedId(null); loadApplicants(); loadStats(); loadCampaigns(); }}
        admin={admin}
        viewMode={currentTab === 'hired' ? 'hired' : 'full'}
      />
    );
  }

  // 최종합격 탭
  if (currentTab === 'hired') {
    return (
      <div className="min-h-screen bg-slate-100">
        <Header admin={admin} onLogout={onLogout} isDev={isDev} showTestAccount={showTestAccount}
          onToggleTestAccount={handleToggleTestAccount} recruitmentOpen={recruitmentOpen} onToggleRecruitment={handleToggleRecruitment} currentTab={currentTab} onTabChange={setTab} />
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-slate-800">최종합격 ({hiredApplicants.length}명)</h2>
            </div>
            {hiredLoading ? (
              <div className="p-8 text-center text-sm text-slate-400">로딩 중...</div>
            ) : hiredApplicants.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-400">최종합격자가 없습니다.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-xs text-slate-500 uppercase">
                      <th className="px-4 py-3 text-left">이름</th>
                      <th className="px-4 py-3 text-left">이메일</th>
                      <th className="px-4 py-3 text-left">포지션</th>
                      <th className="px-4 py-3 text-left">캠페인</th>
                      <th className="px-4 py-3 text-left">합격일</th>
                      <th className="px-4 py-3 text-left"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {hiredApplicants.map((a: any) => (
                      <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedId(a.id)}>
                        <td className="px-4 py-3 font-medium text-slate-800">{a.name}</td>
                        <td className="px-4 py-3 text-slate-600">{a.email}</td>
                        <td className="px-4 py-3 text-slate-600">{a.position || a.offer?.position_title || '-'}</td>
                        <td className="px-4 py-3 text-slate-600">{a.campaign_name || '-'}</td>
                        <td className="px-4 py-3 text-slate-600">{a.offer?.accepted_at ? new Date(a.offer.accepted_at).toLocaleDateString('ko-KR') : '-'}</td>
                        <td className="px-4 py-3">
                          <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }



  return (
    <div className="min-h-screen bg-slate-100">
      <Header admin={admin} onLogout={onLogout} isDev={isDev} showTestAccount={showTestAccount}
        onToggleTestAccount={handleToggleTestAccount} recruitmentOpen={recruitmentOpen} onToggleRecruitment={handleToggleRecruitment} currentTab={currentTab} onTabChange={setTab} />

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* ========== 캠페인 관리 탭 ========== */}
        {currentTab === 'campaigns' && (
          <>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-700">채용 상태</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${activeCampaigns.length > 0 ? 'bg-green-50 text-green-600' : 'bg-slate-100 text-slate-500'}`}>
                  {activeCampaigns.length > 0 ? `${activeCampaigns.length}개 공고 진행중` : '진행중인 공고 없음'}
                </span>
              </div>
              <button onClick={openCreateCampaign}
                className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700">
                + 새 캠페인 만들기
              </button>
            </div>

            {/* 활성 캠페인 */}
            {activeCampaigns.length > 0 && (
              <div className="mb-6">
                <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3">활성 캠페인</h3>
                <div className="space-y-3">
                  {activeCampaigns.map((c) => (
                    <CampaignCard key={c.id} campaign={c} onEdit={() => openEditCampaign(c)}
                      onToggle={() => handleToggleCampaign(c.id, c.name, c.is_active)}
                      onClick={() => { setFilterCampaignId(String(c.id)); setTab('pipeline', true); }} />
                  ))}
                </div>
              </div>
            )}

            {activeCampaigns.length === 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-10 text-center mb-6">
                <p className="text-slate-400 text-sm">활성 캠페인이 없습니다. 새 캠페인을 만들어주세요.</p>
              </div>
            )}

            {/* 미배정 지원자 */}
            {unassignedCount > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-amber-600 text-sm font-medium">미배정 지원자 {unassignedCount}명</span>
                  <span className="text-amber-500 text-xs">캠페인에 배정되지 않은 기존 지원자가 있습니다</span>
                </div>
                <button onClick={() => { setFilterCampaignId('unassigned'); setTab('pipeline', true); }}
                  className="text-xs px-3 py-1.5 bg-amber-100 text-amber-700 rounded-lg font-medium hover:bg-amber-200">
                  확인하기
                </button>
              </div>
            )}

            {/* 종료된 캠페인 */}
            {closedCampaigns.length > 0 && (
              <div>
                <button onClick={() => setShowClosedCampaigns(!showClosedCampaigns)}
                  className="flex items-center gap-2 text-xs font-medium text-slate-500 uppercase tracking-wider mb-3 hover:text-slate-700">
                  종료된 캠페인 ({closedCampaigns.length})
                  <svg className={`w-3 h-3 transition-transform ${showClosedCampaigns ? 'rotate-180' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showClosedCampaigns && (
                  <div className="space-y-3 opacity-60">
                    {closedCampaigns.map((c) => (
                      <CampaignCard key={c.id} campaign={c} onEdit={() => openEditCampaign(c)}
                        onToggle={() => handleToggleCampaign(c.id, c.name, c.is_active)}
                        onClick={() => { setFilterCampaignId(String(c.id)); setTab('pipeline', true); }} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* ========== 채용 진행 탭 ========== */}
        {currentTab === 'pipeline' && (
          <>
            {/* 시험 관리 토글 */}
            {showTestManagement ? (
              <div className="mb-4">
                <button onClick={() => setShowTestManagement(false)}
                  className="flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-700 font-medium mb-4">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  지원자 목록으로 돌아가기
                </button>
                <TestManagement onBack={() => setShowTestManagement(false)} />
              </div>
            ) : (
            <>
            {/* 캠페인 필터 + 시험 관리 버튼 */}
            <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-medium text-slate-500">캠페인:</span>
                <FilterBtn label="전체" active={!filterCampaignId} onClick={() => setFilterCampaignId('')} />
                {campaigns.filter((c) => c.is_active).map((c) => {
                  const cnt = Object.values(c.applicant_stats || {}).reduce((a: number, b: any) => a + b, 0);
                  return (
                    <FilterBtn key={c.id} label={`${c.name} (${cnt})`} active={filterCampaignId === String(c.id)}
                      onClick={() => setFilterCampaignId(String(c.id))} />
                  );
                })}
                {unassignedCount > 0 && (
                  <FilterBtn label={`미배정 (${unassignedCount})`} active={filterCampaignId === 'unassigned'}
                    onClick={() => setFilterCampaignId('unassigned')} />
                )}
                <button onClick={() => setShowTestManagement(true)}
                  className="ml-auto text-xs px-3 py-1.5 border border-indigo-300 text-indigo-600 rounded-lg hover:bg-indigo-50 font-medium">
                  시험 관리
                </button>
              </div>
            </div>

            {/* 통계 카드 */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <StatCard label="서류심사" value={(stats.APPLIED || 0) + (stats.AI_REVIEWED || 0)} color="bg-blue-600" />
              <StatCard label="서면평가" value={stats.WRITTEN_TEST || 0} color="bg-amber-600" />
              <StatCard label="면접" value={stats.INTERVIEW || 0} color="bg-indigo-600" />
              <StatCard label="오퍼" value={stats.OFFER || 0} color="bg-green-600" />
            </div>

            {/* 상태 필터 */}
            <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
              <div className="flex flex-col md:flex-row gap-3">
                <div className="flex flex-wrap gap-2">
                  <FilterBtn label="전체" active={!filterStatus} onClick={() => setFilterStatus('')} />
                  {Object.entries(statusLabels).filter(([key]) => !['HIRED', 'DOC_REJECTED', 'WRITTEN_REJECTED', 'REJECTED'].includes(key)).map(([key, label]) => (
                    <FilterBtn key={key} label={label} active={filterStatus === key} onClick={() => setFilterStatus(key)} />
                  ))}
                </div>
                <form onSubmit={handleSearch} className="flex gap-2 ml-auto">
                  <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="이름/이메일 검색"
                    className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm w-48 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                  <button type="submit" className="px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700">검색</button>
                </form>
              </div>
            </div>

            {/* 지원자 목록 */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-3 py-3 w-10">
                        <input type="checkbox" checked={selectedIds.length === applicants.length && applicants.length > 0}
                          onChange={toggleSelectAll} className="rounded border-slate-300" />
                      </th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">이름</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">캠페인</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">연락처</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">상태</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">지원일</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={7} className="text-center py-10 text-slate-400">로딩 중...</td></tr>
                    ) : applicants.length === 0 ? (
                      <tr><td colSpan={7} className="text-center py-10 text-slate-400">지원자가 없습니다</td></tr>
                    ) : (
                      applicants.map((a) => (
                        <tr key={a.id} className={`border-b border-slate-100 hover:bg-slate-50 ${a.deleted_at ? 'opacity-60 bg-red-50/30' : ''}`}>
                          <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                            {!a.deleted_at && (
                              <input type="checkbox" checked={selectedIds.includes(a.id)}
                                onChange={() => toggleSelect(a.id)} className="rounded border-slate-300" />
                            )}
                          </td>
                          <td className="px-4 py-3 font-medium text-slate-800 cursor-pointer" onClick={() => !a.deleted_at && setSelectedId(a.id)}>
                            {a.name || '-'}
                            {a.deleted_at && (
                              <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-600">삭제됨</span>
                            )}
                            {a.deleted_reason && (
                              <span className="block text-xs text-red-400 mt-0.5">사유: {a.deleted_reason}</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-slate-500 text-xs">
                            {a.campaign_name || <span className="text-amber-500">미배정</span>}
                          </td>
                          <td className="px-4 py-3 text-slate-600">{a.phone ? formatPhone(a.phone) : '-'}</td>
                          <td className="px-4 py-3">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              a.status === 'OFFER' && a.offer_status === 'NEGOTIATING'
                                ? 'bg-orange-100 text-orange-800'
                                : (statusColors[a.status] || 'bg-slate-100 text-slate-600')
                            }`}>
                              {a.status === 'OFFER' && a.offer_status === 'NEGOTIATING'
                                ? '오퍼 재조정중'
                                : (statusLabels[a.status] || a.status)}
                            </span>
                            {a.status === 'WRITTEN_TEST' && a.written_test_progress && (
                              <span className={`ml-1 text-xs px-1.5 py-0.5 rounded font-medium ${
                                a.written_test_progress.submitted === a.written_test_progress.total
                                  ? a.written_test_progress.evaluated === a.written_test_progress.total
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-blue-100 text-blue-700'
                                  : a.written_test_progress.submitted > 0
                                    ? 'bg-yellow-100 text-yellow-700'
                                    : 'bg-slate-100 text-slate-500'
                              }`}>
                                {a.written_test_progress.evaluated === a.written_test_progress.total
                                  ? '평가완료'
                                  : a.written_test_progress.submitted === a.written_test_progress.total
                                    ? '제출완료'
                                    : a.written_test_progress.submitted > 0
                                      ? `${a.written_test_progress.submitted}/${a.written_test_progress.total} 진행중`
                                      : '미시작'}
                              </span>
                            )}
                            {a.status === 'INTERVIEW' && a.interview_status && (
                              <span className={`ml-1 text-xs px-1.5 py-0.5 rounded font-medium ${
                                a.interview_status === 'COMPLETED' ? 'bg-green-100 text-green-700'
                                : a.interview_status === 'CONFIRMED' ? 'bg-blue-100 text-blue-700'
                                : a.interview_status === 'CANCELLED' ? 'bg-red-100 text-red-600'
                                : 'bg-yellow-100 text-yellow-700'
                              }`}>
                                {a.interview_status === 'COMPLETED' ? '면접완료'
                                : a.interview_status === 'CONFIRMED' ? '일정확정'
                                : a.interview_status === 'CANCELLED' ? '취소'
                                : '일정조율'}
                              </span>
                            )}
                            {a.status === 'OFFER' && a.offer_status && a.offer_status !== 'NEGOTIATING' && (
                              <span className={`ml-1 text-xs px-1.5 py-0.5 rounded font-medium ${
                                a.offer_status === 'ACCEPTED' ? 'bg-green-100 text-green-700'
                                : a.offer_status === 'REJECTED' ? 'bg-red-100 text-red-600'
                                : a.offer_status === 'SENT' ? 'bg-blue-100 text-blue-700'
                                : 'bg-slate-100 text-slate-500'
                              }`}>
                                {a.offer_status === 'ACCEPTED' ? '수락'
                                : a.offer_status === 'REJECTED' ? '거절'
                                : a.offer_status === 'SENT' ? '발송됨'
                                : '작성중'}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-slate-500">{a.submitted_at ? new Date(a.submitted_at).toLocaleDateString('ko-KR') : '-'}</td>
                          <td className="px-4 py-3 flex items-center gap-1">
                            {a.deleted_at ? (
                              <button onClick={async (e) => {
                                e.stopPropagation();
                                const ok = await confirm({ title: '지원자 원복', message: `${a.name} 지원자를 원복하시겠습니까?`, confirmText: '원복' });
                                if (!ok) return;
                                try {
                                  await adminApi.restoreApplicant(a.id);
                                  toast.success('지원자가 원복되었습니다.');
                                  loadApplicants();
                                } catch { toast.error('원복 실패'); }
                              }}
                                className="text-xs px-2 py-1 bg-green-50 text-green-600 rounded hover:bg-green-100 font-medium">
                                원복
                              </button>
                            ) : (
                              <>
                                <button onClick={() => setSelectedId(a.id)} className="text-slate-400 hover:text-slate-600">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                </button>
                                <button onClick={(e) => { e.stopPropagation(); handleDeleteApplicant(a); }}
                                  className="text-slate-300 hover:text-red-500 ml-1" title="삭제">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {/* 하단 액션바 */}
              <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between">
                {selectedIds.length > 0 ? (
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-medium text-indigo-600">{selectedIds.length}명 선택됨</span>
                    <select onChange={(e) => { if (e.target.value) handleBulkAssign(parseInt(e.target.value)); e.target.value = ''; }}
                      className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 text-slate-600" defaultValue="">
                      <option value="" disabled>캠페인 배정</option>
                      {campaigns.filter((c) => c.is_active).map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div />
                )}
                <span className="text-xs text-slate-500">총 {total}명</span>
              </div>
            </div>
            </>
            )}
          </>
        )}

        {/* ========== 전체 지원자 탭 ========== */}
        {currentTab === 'all' && (
          <>
            {/* 캠페인 필터 */}
            <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-medium text-slate-500">캠페인:</span>
                <FilterBtn label="전체" active={!filterCampaignId} onClick={() => setFilterCampaignId('')} />
                {campaigns.map((c) => {
                  const cnt = Object.values(c.applicant_stats || {}).reduce((a: number, b: any) => a + b, 0);
                  return (
                    <FilterBtn key={c.id} label={`${c.name} (${cnt})`} active={filterCampaignId === String(c.id)}
                      onClick={() => setFilterCampaignId(String(c.id))} />
                  );
                })}
                {unassignedCount > 0 && (
                  <FilterBtn label={`미배정 (${unassignedCount})`} active={filterCampaignId === 'unassigned'}
                    onClick={() => setFilterCampaignId('unassigned')} />
                )}
              </div>
            </div>

            {/* 통계 카드 */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <StatCard label="전체 지원자" value={totalApplicants} color="bg-slate-600" />
              <StatCard label="진행중" value={(stats.DRAFT || 0) + (stats.APPLIED || 0) + (stats.AI_REVIEWED || 0) + (stats.WRITTEN_TEST || 0) + (stats.INTERVIEW || 0) + (stats.OFFER || 0)} color="bg-blue-600" />
              <StatCard label="입사확정" value={stats.HIRED || 0} color="bg-green-600" />
              <StatCard label="불합격" value={(stats.DOC_REJECTED || 0) + (stats.WRITTEN_REJECTED || 0) + (stats.REJECTED || 0)} color="bg-red-600" />
            </div>

            {/* 상태 필터 */}
            <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
              <div className="flex flex-col md:flex-row gap-3">
                <div className="flex flex-wrap gap-2">
                  <FilterBtn label="전체" active={!filterStatus} onClick={() => setFilterStatus('')} />
                  {Object.entries(statusLabels).map(([key, label]) => (
                    <FilterBtn key={key} label={label} active={filterStatus === key} onClick={() => setFilterStatus(key)} />
                  ))}
                  <label className="flex items-center gap-1.5 ml-2 cursor-pointer">
                    <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)}
                      className="w-3.5 h-3.5 rounded border-slate-300 text-red-600 focus:ring-red-500" />
                    <span className="text-xs text-slate-500">삭제된 지원자</span>
                  </label>
                </div>
                <form onSubmit={handleSearch} className="flex gap-2 ml-auto">
                  <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="이름/이메일 검색"
                    className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm w-48 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                  <button type="submit" className="px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700">검색</button>
                </form>
              </div>
            </div>

            {/* 지원자 목록 */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left px-4 py-3 font-medium text-slate-600">이름</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">캠페인</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">연락처</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">상태</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">지원일</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={6} className="text-center py-10 text-slate-400">로딩 중...</td></tr>
                    ) : applicants.length === 0 ? (
                      <tr><td colSpan={6} className="text-center py-10 text-slate-400">지원자가 없습니다</td></tr>
                    ) : (
                      applicants.map((a) => (
                        <tr key={a.id} className={`border-b border-slate-100 hover:bg-slate-50 ${a.deleted_at ? 'opacity-60 bg-red-50/30' : ''}`}>
                          <td className="px-4 py-3 font-medium text-slate-800 cursor-pointer" onClick={() => !a.deleted_at && setSelectedId(a.id)}>
                            {a.name || '-'}
                            {a.deleted_at && (
                              <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-600">삭제됨</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-slate-500 text-xs">
                            {a.campaign_name || <span className="text-amber-500">미배정</span>}
                          </td>
                          <td className="px-4 py-3 text-slate-600">{a.phone ? formatPhone(a.phone) : '-'}</td>
                          <td className="px-4 py-3">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColors[a.status] || 'bg-slate-100 text-slate-600'}`}>
                              {statusLabels[a.status] || a.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-500">{a.submitted_at ? new Date(a.submitted_at).toLocaleDateString('ko-KR') : '-'}</td>
                          <td className="px-4 py-3">
                            {a.deleted_at ? (
                              <button onClick={async (e) => {
                                e.stopPropagation();
                                const ok = await confirm({ title: '지원자 원복', message: `${a.name} 지원자를 원복하시겠습니까?`, confirmText: '원복' });
                                if (!ok) return;
                                try {
                                  await adminApi.restoreApplicant(a.id);
                                  toast.success('지원자가 원복되었습니다.');
                                  loadApplicants();
                                } catch { toast.error('원복 실패'); }
                              }}
                                className="text-xs px-2 py-1 bg-green-50 text-green-600 rounded hover:bg-green-100 font-medium">
                                원복
                              </button>
                            ) : (
                              <button onClick={() => setSelectedId(a.id)} className="text-slate-400 hover:text-slate-600">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-end">
                <span className="text-xs text-slate-500">총 {total}명</span>
              </div>
            </div>
          </>
        )}
      </main>

      {/* 캠페인 생성/수정 모달 */}
      {showCampaignModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">
              {editingCampaign ? '캠페인 수정' : '새 캠페인 만들기'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">캠페인명 *</label>
                <input type="text" value={campaignForm.name}
                  onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })}
                  placeholder="2026년도 인턴십 프로그램 1차"
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">설명 (선택)</label>
                <input type="text" value={campaignForm.description}
                  onChange={(e) => setCampaignForm({ ...campaignForm, description: e.target.value })}
                  placeholder="사람인/리멤버 동시 공고 예정"
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">모집 포지션 (선택)</label>
                <div className="border border-slate-200 rounded-lg bg-slate-50 p-3">
                  {availablePositions.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                      {availablePositions.map((pos: any) => (
                        <label key={pos.id} className="flex items-center gap-2 cursor-pointer group">
                          <input
                            type="checkbox"
                            checked={selectedPositionIds.includes(pos.id)}
                            onChange={(e) => {
                              setSelectedPositionIds(
                                e.target.checked
                                  ? [...selectedPositionIds, pos.id]
                                  : selectedPositionIds.filter((id) => id !== pos.id)
                              );
                            }}
                            className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="text-sm text-slate-700 group-hover:text-indigo-700 leading-tight">{pos.full_name || pos.name}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 py-2">등록된 포지션이 없습니다.</p>
                  )}
                  <div className="flex gap-2 mt-3 pt-3 border-t border-slate-200">
                    <input
                      type="text"
                      value={newPositionName}
                      onChange={(e) => setNewPositionName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddPosition())}
                      placeholder="새 포지션 추가"
                      className="flex-1 px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <button
                      type="button"
                      onClick={handleAddPosition}
                      disabled={addingPosition || !newPositionName.trim()}
                      className="px-3 py-1.5 text-sm rounded-lg font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {addingPosition ? '추가 중...' : '추가'}
                    </button>
                  </div>
                </div>
                <p className="text-xs text-slate-400 mt-1">AI 서류심사 시 직무적합도 평가에 활용됩니다.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">시작일 *</label>
                  <input type="date" value={campaignForm.start_date}
                    onChange={(e) => setCampaignForm({ ...campaignForm, start_date: e.target.value })}
                    className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">종료일 *</label>
                  <input type="date" value={campaignForm.end_date}
                    onChange={(e) => setCampaignForm({ ...campaignForm, end_date: e.target.value })}
                    className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowCampaignModal(false)}
                className="flex-1 py-2.5 border border-slate-300 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50">
                취소
              </button>
              <button onClick={handleSaveCampaign}
                disabled={!campaignForm.name.trim() || !campaignForm.start_date || !campaignForm.end_date}
                className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg disabled:opacity-50">
                {editingCampaign ? '수정' : '캠페인 생성'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 삭제 확인 모달 */}
      {deleteStep > 0 && deleteTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            {deleteStep === 1 ? (
              <>
                <div className="text-center mb-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-800">지원자 삭제</h3>
                  <p className="text-sm text-slate-500 mt-2">
                    <strong>{deleteTarget.name}</strong> ({deleteTarget.email})<br/>
                    지원자를 정말 삭제하시겠습니까?
                  </p>
                  <p className="text-xs text-slate-400 mt-2">삭제된 데이터는 이력으로 보관됩니다.</p>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setDeleteStep(0)} className="flex-1 py-2.5 border border-slate-300 text-slate-700 text-sm rounded-lg hover:bg-slate-50">취소</button>
                  <button onClick={() => setDeleteStep(2)} className="flex-1 py-2.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700">삭제 진행</button>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-lg font-semibold text-slate-800 mb-1">삭제 사유 입력</h3>
                <p className="text-sm text-slate-500 mb-4">
                  <strong>{deleteTarget.name}</strong> 삭제 사유를 입력해주세요.
                </p>
                <textarea
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  placeholder="삭제 사유를 입력해주세요..."
                  rows={3}
                  autoFocus
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 resize-none mb-4"
                />
                <div className="flex gap-3">
                  <button onClick={() => setDeleteStep(1)} className="flex-1 py-2.5 border border-slate-300 text-slate-700 text-sm rounded-lg hover:bg-slate-50">이전</button>
                  <button onClick={handleDeleteConfirm} disabled={!deleteReason.trim()} className="flex-1 py-2.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50">최종 삭제</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ========== 하위 컴포넌트 ==========

const EduSettingsPopup: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    adminApi.getEduExpiry().then((res) => {
      setDays(res.edu_expiry_days);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.setEduExpiry(days);
      toast.success('교육 콘텐츠 만료 기간이 변경되었습니다.');
      onClose();
    } catch {
      toast.error('설정 저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50" onClick={onClose} />
      <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-xl shadow-xl border border-slate-200 p-4 z-50">
        <h3 className="text-sm font-semibold text-slate-800 mb-3">교육 콘텐츠 설정</h3>
        {loading ? (
          <p className="text-xs text-slate-400">로딩 중...</p>
        ) : (
          <>
            <label className="block text-xs text-slate-500 mb-1.5">열람 링크 만료 기간</label>
            <div className="flex items-center gap-2 mb-3">
              <input type="number" min={1} max={365} value={days}
                onChange={(e) => setDays(Math.max(1, Math.min(365, parseInt(e.target.value) || 1)))}
                className="w-20 px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              <span className="text-sm text-slate-600">일</span>
            </div>
            <p className="text-xs text-slate-400 mb-3">온보딩 이메일의 교육 자료 열람 링크가 설정한 기간 후 만료됩니다.</p>
            <div className="flex gap-2">
              <button onClick={handleSave} disabled={saving}
                className="flex-1 px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                {saving ? '저장 중...' : '저장'}
              </button>
              <button onClick={onClose}
                className="px-3 py-1.5 text-xs text-slate-500 hover:text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50">
                취소
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
};

const Header: React.FC<{
  admin: any; onLogout: () => void; isDev: boolean; showTestAccount: boolean;
  onToggleTestAccount: () => void; recruitmentOpen: boolean; onToggleRecruitment: () => void;
  currentTab: TabType; onTabChange: (tab: TabType) => void;
}> = ({ admin, onLogout, isDev, showTestAccount, onToggleTestAccount, recruitmentOpen, onToggleRecruitment, currentTab, onTabChange }) => {
  const navigate = useNavigate();
  const [showEduSettings, setShowEduSettings] = useState(false);

  return (
  <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
    <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <img src="/grk-logo.jpg" alt="GRK" className="h-8 object-contain" style={{ mixBlendMode: 'multiply' }} />
        <span className="text-sm font-medium text-slate-600">채용 관리</span>
      </div>
      <div className="flex items-center gap-3">
        {isDev && (
          <button onClick={onToggleRecruitment}
            className={`text-xs px-3 py-1.5 rounded-lg font-medium ${recruitmentOpen ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
            채용 {recruitmentOpen ? '활성' : '비활성'}
          </button>
        )}
        {isDev && (
          <button onClick={onToggleTestAccount}
            className={`text-xs px-3 py-1.5 rounded-lg font-medium ${showTestAccount ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
            테스트 계정 {showTestAccount ? '공개' : '비공개'}
          </button>
        )}
        <button onClick={() => navigate('/admin/education')}
          className="text-xs px-3 py-1.5 rounded-lg font-medium bg-blue-50 text-blue-600 hover:bg-blue-100">
          교육 설정
        </button>
        <span className="text-sm text-slate-500">{admin?.name}</span>
        <button onClick={onLogout} className="text-sm text-slate-400 hover:text-slate-600 px-2 py-1 rounded hover:bg-slate-50">
          로그아웃
        </button>
      </div>
    </div>
    {/* 탭 네비게이션 */}
    <div className="max-w-6xl mx-auto px-4">
      <div className="flex gap-1">
        {([
          { key: 'campaigns', label: '캠페인 관리' },
          { key: 'pipeline', label: '채용 진행' },
          { key: 'hired', label: '최종합격' },
          { key: 'all', label: '전체 지원자' },
        ] as { key: TabType; label: string }[]).map((tab) => (
          <button key={tab.key} onClick={() => onTabChange(tab.key)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              currentTab === tab.key
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}>
            {tab.label}
          </button>
        ))}
      </div>
    </div>
  </header>
  );
};

const CampaignCard: React.FC<{
  campaign: any; onEdit: () => void; onToggle?: () => void; onClick?: () => void;
}> = ({ campaign, onEdit, onToggle, onClick }) => {
  const s = campaign.applicant_stats || {};
  const total = Object.values(s).reduce((a: number, b: any) => a + b, 0);
  const startDate = campaign.start_date?.split('T')[0] || '';
  const endDate = campaign.end_date?.split('T')[0] || '';

  return (
    <div className={`bg-white rounded-xl border border-slate-200 p-5 ${onClick ? 'cursor-pointer hover:border-indigo-300 transition-colors' : ''}`}
      onClick={onClick}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <h4 className="text-sm font-semibold text-slate-800">{campaign.name}</h4>
          {campaign.description && <p className="text-xs text-slate-500 mt-0.5">{campaign.description}</p>}
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
          campaign.is_active ? 'bg-green-50 text-green-600' : 'bg-slate-100 text-slate-500'
        }`}>
          {campaign.is_active ? '활성' : '종료'}
        </span>
      </div>
      <p className="text-xs text-slate-400 mb-3">{startDate} ~ {endDate}</p>
      <div className="flex items-center gap-4 text-xs text-slate-600 mb-3">
        <span>지원 <strong>{total}</strong>명</span>
        <span>AI심사 <strong>{s.AI_REVIEWED || 0}</strong></span>
        <span>면접 <strong>{s.INTERVIEW || 0}</strong></span>
        <span>합격 <strong>{s.HIRED || 0}</strong></span>
      </div>
      <div className="flex gap-2 justify-end">
        <button onClick={(e) => { e.stopPropagation(); onEdit(); }} className="text-xs px-3 py-1.5 border border-slate-300 text-slate-600 rounded-lg hover:bg-slate-50 font-medium">
          수정
        </button>
        {onToggle && (
          <button onClick={(e) => { e.stopPropagation(); onToggle(); }}
            className={`text-xs px-3 py-1.5 border rounded-lg font-medium ${
              campaign.is_active
                ? 'border-red-200 text-red-500 hover:bg-red-50'
                : 'border-green-200 text-green-600 hover:bg-green-50'
            }`}>
            {campaign.is_active ? '비활성화' : '활성화'}
          </button>
        )}
      </div>
    </div>

  );
};

const StatCard: React.FC<{ label: string; value: number; color: string }> = ({ label, value, color }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4">
    <p className="text-xs text-slate-500 mb-1">{label}</p>
    <div className="flex items-center gap-2">
      <div className={`w-2 h-2 rounded-full ${color}`} />
      <span className="text-xl font-semibold text-slate-800">{value}</span>
    </div>
  </div>
);

const FilterBtn: React.FC<{ label: string; active: boolean; onClick: () => void }> = ({ label, active, onClick }) => (
  <button onClick={onClick}
    className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${
      active ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
    }`}>
    {label}
  </button>
);

export default AdminDashboard;
